import React from 'react';
import PropTypes from 'prop-types';
import * as RadixLabel from '@radix-ui/react-label';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@radix-ui/react-icons';
import {
  inputCls,
  labelClass,
  errorTextClass,
} from 'modules/Common/form/v2/primitives/inputClasses';

/**
 * Field primitives for the onboarding wizard. The wizard manages its own
 * state (no react-hook-form), so these are thin wrappers around the v2
 * input class names + Radix Select — keeping the same look as CgDialog
 * forms without pulling in Controller wiring.
 */

export const WizardLabel = ({ htmlFor, required, children }) => (
  <RadixLabel.Root
    htmlFor={htmlFor}
    className={labelClass}
  >
    {children}
    {required ? <span className="text-red-500 ml-0.5">*</span> : null}
  </RadixLabel.Root>
);

WizardLabel.propTypes = {
  htmlFor: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.node,
};

export const WizardError = ({ children }) => {
  if (!children) return null;
  return <p className={errorTextClass}>{children}</p>;
};

WizardError.propTypes = { children: PropTypes.node };

export const WizardFieldGroup = ({
  id,
  label,
  required,
  error,
  description,
  children,
  className = '',
}) => (
  <div className={`mb-4 ${className}`}>
    {label ? (
      <div className="mb-1">
        <WizardLabel
          htmlFor={id}
          required={required}
        >
          {label}
        </WizardLabel>
      </div>
    ) : null}
    {description ? <p className="text-xs text-slate-500 mb-1.5 -mt-0.5">{description}</p> : null}
    {children}
    <WizardError>{error}</WizardError>
  </div>
);

WizardFieldGroup.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node,
  required: PropTypes.bool,
  error: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

export const WizardInput = React.forwardRef(
  ({ id, value, onChange, error, type = 'text', maxLength, placeholder, autoFocus }, ref) => (
    <input
      ref={ref}
      id={id}
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
      placeholder={placeholder}
      autoFocus={autoFocus}
      aria-invalid={!!error}
      className={inputCls(!!error)}
    />
  ),
);
WizardInput.displayName = 'WizardInput';
WizardInput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  type: PropTypes.string,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
};

/**
 * Radix-based select. Filters out the leading "" placeholder option that
 * the legacy constants include — Radix uses the trigger's placeholder for
 * the empty state instead of an in-list "Select…" entry.
 */
export const WizardSelect = ({ id, value, onChange, options, placeholder, error, disabled }) => {
  const items = options.filter((o) => o.value !== '');
  const selected = items.find((it) => it.value === value);
  const radixValue = value == null || value === '' ? undefined : String(value);

  return (
    <RadixSelect.Root
      value={radixValue}
      onValueChange={onChange}
      disabled={disabled}
    >
      <RadixSelect.Trigger
        id={id}
        aria-invalid={!!error}
        className={inputCls(!!error, 'inline-flex items-center justify-between')}
      >
        {selected ? (
          <span className="truncate text-left">{selected.label}</span>
        ) : (
          <span className="truncate text-left text-slate-400">{placeholder}</span>
        )}
        <RadixSelect.Icon className="text-slate-400">
          <ChevronDownIcon />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          style={{
            width: 'var(--radix-select-trigger-width)',
            minWidth: 'var(--radix-select-trigger-width)',
            zIndex: 99998,
          }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 max-h-[300px] overflow-hidden"
        >
          <RadixSelect.ScrollUpButton className="flex items-center justify-center h-6 bg-white text-slate-400">
            <ChevronUpIcon />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="p-1">
            {items.map((it) => (
              <RadixSelect.Item
                key={it.value}
                value={it.value}
                className="relative flex items-center px-3 py-2 text-sm rounded-md cursor-pointer select-none
                  data-[highlighted]:bg-primaryColor/10 data-[highlighted]:text-slate-800 data-[highlighted]:outline-none
                  data-[state=checked]:font-medium"
              >
                <RadixSelect.ItemText>{it.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="ml-auto pl-3 text-primaryColor">
                  <CheckIcon />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="flex items-center justify-center h-6 bg-white text-slate-400">
            <ChevronDownIcon />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

WizardSelect.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};
